import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getConsent } from "@/components/CookieConsent";

/**
 * Analytics loader — RGPD-friendly, env-driven, zero-config.
 *
 * Supports two backends via env vars set in /app/frontend/.env:
 *   REACT_APP_PLAUSIBLE_DOMAIN  — e.g. "anatradex.com"
 *                                 Plausible: cookie-less, no consent banner required,
 *                                 pay-what-you-use (~9€/mo), open source.
 *   REACT_APP_POSTHOG_KEY       — e.g. "phc_xxxxxxxxxxxx"
 *                                 PostHog: free up to 1M events/mo, session replay,
 *                                 heatmaps. Slightly heavier privacy footprint.
 *
 * If BOTH are set, both run in parallel. If NEITHER is set, no tracking at all
 * (dev-mode default — nothing leaks). Route changes trigger virtual pageviews
 * on both — critical for SPAs since neither library auto-detects React Router.
 */

let scriptsLoaded = false;

function loadPlausible(domain) {
  if (document.querySelector('script[data-plausible]')) return;
  const s = document.createElement("script");
  s.defer = true;
  s.setAttribute("data-plausible", "1");
  s.setAttribute("data-domain", domain);
  s.src = "https://plausible.io/js/script.js";
  document.head.appendChild(s);
}

function loadPostHog(apiKey) {
  if (window.posthog) return;
  // Minimal loader — same as PostHog's official snippet, condensed
  const p = document.createElement("script");
  p.async = true;
  p.src = "https://us-assets.i.posthog.com/static/array.js";
  p.setAttribute("data-posthog", "1");
  document.head.appendChild(p);
  window.posthog = window.posthog || [];
  window.posthog._i = [];
  window.posthog.init = function (key, cfg) {
    window.posthog._i.push([key, cfg]);
  };
  window.posthog.init(apiKey, {
    api_host: "https://us.i.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: false, // We fire manually on route change
  });
}

/**
 * Fire a virtual pageview event on both backends. Called by <Analytics /> on
 * every location change.
 */
function trackPageview(path) {
  try {
    // Plausible auto-tracks pushState, so we just trigger a manual event to be safe
    if (window.plausible) window.plausible("pageview");
  } catch { /* noop */ }
  try {
    if (window.posthog?.capture) window.posthog.capture("$pageview", { $pathname: path });
  } catch { /* noop */ }
}

export default function Analytics() {
  const location = useLocation();
  const plausibleDomain = process.env.REACT_APP_PLAUSIBLE_DOMAIN?.trim();
  const posthogKey = process.env.REACT_APP_POSTHOG_KEY?.trim();

  // Load provider scripts once — only when configured AND RGPD consent = "all"
  useEffect(() => {
    const tryLoad = () => {
      if (scriptsLoaded) return;
      if (getConsent()?.level !== "all") return;
      if (plausibleDomain) loadPlausible(plausibleDomain);
      if (posthogKey) loadPostHog(posthogKey);
      scriptsLoaded = true;
    };
    tryLoad();
    window.addEventListener("anatradex-consent", tryLoad);
    return () => window.removeEventListener("anatradex-consent", tryLoad);
  }, [plausibleDomain, posthogKey]);

  // Fire pageview on route change (no-ops if scripts never loaded)
  useEffect(() => {
    if (!plausibleDomain && !posthogKey) return;
    if (!scriptsLoaded) return;
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search, plausibleDomain, posthogKey]);

  return null;
}

/**
 * Programmatic event capture — call from anywhere in the app to record a custom
 * event (e.g. "signup_started", "backtest_run", "beta_application_submitted").
 * Silently no-ops when analytics are disabled or not yet loaded.
 */
export function trackEvent(name, props = {}) {
  try {
    if (window.plausible) window.plausible(name, { props });
  } catch { /* noop */ }
  try {
    if (window.posthog?.capture) window.posthog.capture(name, props);
  } catch { /* noop */ }
}
